import {
    Card, CardContent, IconButton,
    Stack, Tooltip, Typography, Skeleton,
    CircularProgress, Grid, FormControl, RadioGroup, FormControlLabel, Radio, useTheme, Container
} from "@mui/material";
import Box from "@mui/material/Box";
import { Message } from "../types/courseTypes";
import DeleteIcon from '@mui/icons-material/Delete';
import CachedIcon from '@mui/icons-material/Cached';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CopyButton from "./CopyButton";
import html2canvas from 'html2canvas';
import jsPDF from "jspdf";
import SAILLogo from '../SAIL.svg';
import React from "react";

interface EvaluationRatingMessageProps {
    message: Message;
    deleteMessage: (id: number) => void;
    loading?: boolean;
    disableEditDelete?: boolean;
}

const sailCategories = [
    {
        id: 1,
        name: 'Impacts of AI',
        description: (
            <ul>
                <li>People, Organisations, and AI</li>
                <li>Engagement with AI</li>
                <li>AI as a Technology</li>
                <li>AI in Context</li>
                <li>Indigenous Perspectives on AI</li>
            </ul>
        ),
    },
    {
        id: 2,
        name: 'What AI is & How it Works',
        description: (
            <ul>
                <li>AI Terms and Features</li>
                <li>Types of AI</li>
                <li>AI and Data</li>
            </ul>
        ),
    },
    {
        id: 3,
        name: 'Cognitive Skills',
        description: (
            <ul>
                <li>Understanding AI in Use</li>
                <li>Critical Application of AI</li>
                <li>AI Transparency</li>
                <li>Associated Literacies and Learning</li>
                <li>AI in Research</li>
            </ul>
        ),
    },
    {
        id: 4,
        name: 'Applied Skills',
        description: (
            <ul>
                <li>Selecting and Applying AI</li>
                <li>Goal-based AI Application and Development</li>
                <li>Using and Creating AI Tools</li>
                <li>AI in Projects</li>
                <li>AI Technologies</li>
                <li>Data in AI Applications</li>
            </ul>
        ),
    },
    {
        id: 5,
        name: 'Social, Cultural & Ethical Issues',
        description: (
            <ul>
                <li>AI and Society</li>
                <li>Ethical Issues in AI</li>
                <li>AI and Culture</li>
                <li>AI Bias</li>
                <li>AI Policy</li>
                <li>AI and Equity</li>
            </ul>
        ),
    },
    {
        id: 6,
        name: 'Risks & Mitigations',
        description: (
            <ul>
                <li>Risks of AI Systems</li>
                <li>Mitigating the Risks of AI</li>
                <li>Safe and Responsible Use of AI</li>
                <li>AI and Cultural Diversity</li>
            </ul>
        ),
    },
];

const EvaluationRatingMessage: React.FC<EvaluationRatingMessageProps> = ({
                                                                             message,
                                                                             deleteMessage,
                                                                             loading = false,
                                                                             disableEditDelete = false
                                                                         }) => {
    const theme = useTheme();
    const inputRatings = /^[0-4]{6}$/.test(message.inputText)
        ? message.inputText.split("").map(Number)
        : Array(6).fill(0); // Fallback to zeros if invalid

    // Process summary to handle **bold** and ### headings
    const processedSummary = message.responseText
        .replace(/\*\*(.*?)\*\*/g, '<strong style="font-size: 1.1em;">$1</strong>')
        .replace(/###\s*(.*?)(?=\n|$)/g, '<strong style="font-size: 1.25em; font-weight: bold;">$1</strong>');

    const handleExportToPDF = () => {
        const input = document.getElementById(`response-${message.id}`);
        if (!input) {
            console.error('Element not found for PDF export');
            return;
        }

        html2canvas(input, { scale: 2 }).then(canvas => {
            const imgData = canvas.toDataURL('image/png');
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            const imgProps = doc.getImageProperties(imgData);
            const pdfWidth = doc.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            let position = 10;

            doc.addImage(imgData, 'PNG', 10, position, pdfWidth - 20, pdfHeight);
            if (pdfHeight > doc.internal.pageSize.getHeight() - 20) {
                doc.addPage();
                position = 10;
                doc.addImage(imgData, 'PNG', 10, position, pdfWidth - 20, pdfHeight);
            }

            doc.save(`evaluation_${message.id}.pdf`);
        });
    };

    const getRadioHighlightColor = (rating: number) => {
        if (rating === 0) {
            return theme.palette.error.main || '#d32f2f'; // Red for None
        }
        if (rating <= 2) {
            return theme.palette.warning.main || '#f57c00'; // Yellow for 1-2
        }
        return theme.palette.success.main || '#388e3c'; // Green for 3-4
    };


    return (
        <Container maxWidth="md">
            {/* Timestamp */}
            <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                >
                    {new Date(message.createdAt).toLocaleString('en-NZ', { dateStyle: 'short', timeStyle: 'short' })}
                </Typography>
            </Box>
            {/* User Message (Ratings Grid with RadioGroup) */}
            <Box
                sx={{
                    alignSelf: 'flex-end',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-end',
                    '&:hover .hover-buttons': { opacity: 1 },
                }}
            >
                {loading ? (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {sailCategories.map((category) => (
                            <Grid size={6} key={category.id}>
                                <Card
                                    sx={{
                                        backgroundColor: 'background.paper',
                                        minHeight: '400px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Box sx={{ mb: 1 }}>
                                            <Typography variant="h6">
                                                <Skeleton width="60%" />
                                            </Typography>
                                        </Box>
                                        <Box sx={{ flexGrow: 1, mb: 2 }}>
                                            <Skeleton variant="text" height={20} />
                                            <Skeleton variant="text" height={20} />
                                            <Skeleton variant="text" height={20} />
                                        </Box>
                                        <Box sx={{ mt: 2 }}>
                                            <Skeleton variant="rectangular" height={40} />
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {sailCategories.map((category, index) => (
                            <Grid size={6} key={category.id}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        minHeight: '315px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                        <Box>
                                            <Typography variant="h6">{category.name}</Typography>
                                        </Box>
                                        <Box sx={{ flexGrow: 1}}>{category.description}</Box>
                                        <FormControl component="fieldset">
                                            <RadioGroup
                                                row
                                                value={inputRatings[index].toString()}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    width: '100%',
                                                    position: 'relative',
                                                }}
                                                aria-label={`Likert scale for ${category.name}`}
                                            >
                                                {['0', '1', '2', '3', '4'].map((val) => (
                                                    <Box
                                                        key={val}
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            flex: 1,
                                                            position: 'relative',
                                                            zIndex: 1,
                                                            '&:not(:last-child):after': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: '15px',
                                                                left: '50%',
                                                                width: '100%',
                                                                height: '2px',
                                                                backgroundColor: 'grey.400',
                                                                zIndex: 0,
                                                            },
                                                        }}
                                                    >
                                                        <FormControlLabel
                                                            value={val}
                                                            control={
                                                                <Radio
                                                                    sx={{
                                                                        '&.Mui-checked': {
                                                                            color: getRadioHighlightColor(inputRatings[index]),
                                                                        },
                                                                        backgroundColor: 'background.paper',
                                                                        padding: '4px',
                                                                    }}
                                                                />
                                                            }
                                                            label=""
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                mt: -0.5,
                                                                textAlign: 'center',
                                                                maxWidth: '80px',
                                                            }}
                                                        >
                                                            {val === '0' ? 'None' : val === '4' ? 'Strong Focus' : val}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </RadioGroup>
                                        </FormControl>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
                <Stack
                    className="hover-buttons"
                    direction="row"
                    spacing={1}
                    sx={{
                        mt: 1,
                        opacity: 0,
                        transition: 'opacity 0.2s',
                    }}
                >
                    <CopyButton textToCopy={message.inputText} />
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => deleteMessage(message.id)}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>
            {/* AI Response (Reused from MessageArray) */}
            <Box
                sx={{
                    alignSelf: 'flex-start',
                    textAlign: 'left',
                    mb: 10,
                    width: '100%',
                    position: 'relative',
                    overflow: 'visible',
                }}
            >
                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        alignItems: 'flex-start',
                    }}
                >
                    {/* SAIL Logo with Spinning CircularProgress */}
                    <Box
                        sx={{
                            position: 'absolute',
                            left: -40,
                            top: 0,
                            width: '100%',
                            maxWidth: 35,
                            zIndex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Box sx={{ position: 'relative' }}>
                            <img
                                src={SAILLogo}
                                alt="SAIL Logo"
                                style={{
                                    width: 40,
                                    height: 'auto',
                                    transition: 'transform 0.5s ease',
                                    ...(loading ? {
                                        transform: 'scale(0.8)',
                                        animation: 'shrink 0.5s ease-in',
                                        '@keyframes shrink': {
                                            '0%': { transform: 'scale(1)' },
                                            '100%': { transform: 'scale(0.8)' },
                                        },
                                    } : {
                                        transform: 'scale(1)',
                                        animation: 'grow 0.5s ease-out',
                                        '@keyframes grow': {
                                            '0%': { transform: 'scale(0.8)' },
                                            '100%': { transform: 'scale(1)' },
                                        },
                                    }),
                                }}
                            />
                            {loading && (
                                <React.Fragment>
                                    <svg width={0} height={0}>
                                        <defs>
                                            <linearGradient id="my_gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                <stop offset="0%" stopColor="grey" />
                                                <stop offset="100%" stopColor="black" />
                                            </linearGradient>
                                        </defs>
                                    </svg>
                                    <CircularProgress
                                        size={50}
                                        sx={{
                                            'svg circle': { stroke: 'url(#my_gradient)' },
                                            color: 'inherit',
                                            position: 'absolute',
                                            top: -4,
                                            left: -5,
                                            zIndex: 1,
                                        }}
                                    />
                                </React.Fragment>
                            )}
                        </Box>
                    </Box>
                    {/* Response Content */}
                    <Box sx={{ flex: 1, ml: 6 }} id={`response-${message.id}`}>
                        {loading ? (
                            <Box sx={{ position: 'relative', width: '100%' }}>
                                <Stack>
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={40}
                                        sx={{ mt: 1, ml: 25 }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={40}
                                        sx={{ mt: 1 }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={40}
                                        sx={{ mt: 1 }}
                                    />
                                    <Skeleton
                                        variant="text"
                                        animation="wave"
                                        height={40}
                                        sx={{ mt: 1 }}
                                    />
                                </Stack>
                                <Typography
                                    variant="subtitle2"
                                    fontSize="1rem"
                                    sx={{
                                        position: 'absolute',
                                        top: 5,
                                        left: 10,
                                    }}
                                >
                                    Fetching Evaluation...
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <Typography
                                    variant="body1"
                                    component="div"
                                    sx={{ whiteSpace: 'pre-wrap' }}
                                    dangerouslySetInnerHTML={{
                                        __html: processedSummary,
                                    }}
                                />
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{ mt: 1 }}
                                >
                                    <Tooltip title="Regenerate">
                                        <IconButton size="small" disabled={loading}>
                                            <CachedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                    <CopyButton textToCopy={processedSummary || message.responseText} disabled={loading} />
                                    <Tooltip title="Export to PDF">
                                        <IconButton size="small" onClick={handleExportToPDF} disabled={loading}>
                                            <FileDownloadIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </>
                        )}
                    </Box>
                </Stack>
            </Box>
        </Container>
    );
};

export default EvaluationRatingMessage;